import * as T from 'three';

/** Repair a nearly cyclic locomotion take once, at ingest. A short Hermite
 * bridge runs THROUGH the wrap (tail -> head), matching pose and velocity at
 * both joins. The middle of the authored stride is left alone. Rotations use
 * a local quaternion-log chart, so no Euler wrap or quaternion sign flip can
 * send a limb around the long way. No runtime blending or extra pose history. */
export function closeLocomotionLoop(clip:T.AnimationClip,halfWindow=.10):T.AnimationClip {
  const duration=clip.duration,window=Math.min(halfWindow,duration*.2);
  if(window<=0)return clip;
  const start=duration-window,end=window,span=2*window,h=Math.min(1/240,window*.1);
  const steps=Math.max(2,Math.ceil(duration*120)),seamTimes=[0,window,start,duration];
  for(let i=1;i<steps;i++){
    const time=duration*i/steps;
    if(time<window||time>start)seamTimes.push(time);
  }
  const q=new T.Quaternion(),base=new T.Quaternion(),inverse=new T.Quaternion();
  const tracks=clip.tracks.map(track=>{
    const rotation=track instanceof T.QuaternionKeyframeTrack;
    if(!rotation&&!(track instanceof T.VectorKeyframeTrack))return track;
    // Retain every original key: resampling the middle could smooth away an
    // authored knee/push-off extremum even though it is outside this repair.
    const keys=[...track.times,...seamTimes].filter(t=>t>=0&&t<=duration).sort((a,b)=>a-b);
    const unique=keys.filter((t,i)=>i===0||t-keys[i-1]>1e-6);
    unique[0]=0;unique[unique.length-1]=duration;
    const times=new Float32Array(unique);
    const sample=track.createInterpolant(),size=rotation?4:3;
    if(rotation){base.fromArray(sample.evaluate(start)).normalize();inverse.copy(base).invert();}
    const value=(time:number):number[]=>{
      const v=sample.evaluate(time);
      if(!rotation)return Array.from(v);
      q.fromArray(v).normalize().premultiply(inverse);
      if(q.w<0)q.set(-q.x,-q.y,-q.z,-q.w);
      const s=Math.hypot(q.x,q.y,q.z),k=s<1e-8?2:2*Math.atan2(s,q.w)/s;
      return [q.x*k,q.y*k,q.z*k];
    };
    const a=value(start),b=value(end),a0=value(start-h),a1=value(start+h),b0=value(end-h),b1=value(end+h);
    const va=a.map((_,i)=>(a1[i]-a0[i])/(2*h)),vb=b.map((_,i)=>(b1[i]-b0[i])/(2*h));
    const values=new Float32Array(times.length*size);
    for(let i=0;i<times.length;i++){
      const time=times[i];
      if(time>window&&time<start){
        const v=sample.evaluate(time);for(let k=0;k<size;k++)values[i*size+k]=v[k];
        continue;
      }
      const t=(time>=start?time-start:time+window)/span,t2=t*t,t3=t2*t;
      const v=a.map((x,k)=>(2*t3-3*t2+1)*x+(t3-2*t2+t)*span*va[k]
        +(-2*t3+3*t2)*b[k]+(t3-t2)*span*vb[k]);
      if(rotation){
        const angle=Math.hypot(...v),scale=angle<1e-8?.5:Math.sin(angle/2)/angle;
        q.set(v[0]*scale,v[1]*scale,v[2]*scale,Math.cos(angle/2)).premultiply(base).normalize();
        q.toArray(values,i*4);
      }else values.set(v,i*3);
    }
    if(rotation){
      // Keep adjacent keys in one quaternion hemisphere, including the wrap.
      for(let i=4;i<values.length;i+=4){
        let dot=0;for(let k=0;k<4;k++)dot+=values[i+k]*values[i-4+k];
        if(dot<0)for(let k=0;k<4;k++)values[i+k]*=-1;
      }
      return new T.QuaternionKeyframeTrack(track.name,times,values);
    }
    // Preserve the centered pelvis trajectory after changing the seam's shape.
    for(let axis=0;axis<3;axis++){
      let oldMean=0,newMean=0;
      for(let i=axis;i<track.values.length;i+=3)oldMean+=track.values[i]/(track.values.length/3);
      for(let i=axis;i<values.length;i+=3)newMean+=values[i]/(values.length/3);
      for(let i=axis;i<values.length;i+=3)values[i]+=oldMean-newMean;
    }
    return new T.VectorKeyframeTrack(track.name,times,values);
  });
  return new T.AnimationClip(clip.name,duration,tracks,clip.blendMode);
}

/** Periodic, zero-phase filtering removes capture chatter from the torso without
 * adding input latency. Legs retain their authored contacts and push-off timing.
 * The 30 ms kernel keeps the broad hip sway, with identical samples at the wrap. */
export function smoothLocomotionTorso(clip:T.AnimationClip):T.AnimationClip {
  const duration=clip.duration,steps=Math.ceil(duration*120),sigma=.03;
  const times=Float32Array.from({length:steps+1},(_,i)=>i/steps*duration);
  const base=new T.Quaternion(),q=new T.Quaternion();
  const tracks=clip.tracks.map(track=>{
    if(!/\.bones\[(pelvis|spine_0[123]|neck_01|head)\]\./.test(track.name))return track;
    const rotation=track instanceof T.QuaternionKeyframeTrack;
    if(!rotation&&!(track instanceof T.VectorKeyframeTrack))return track;
    const size=rotation?4:3,values=new Float32Array(times.length*size),sample=track.createInterpolant();
    for(let i=0;i<steps;i++){
      const time=times[i],sum=new Array(size).fill(0);let total=0;
      if(rotation)base.fromArray(sample.evaluate(time));
      for(let j=-9;j<=9;j++){
        const offset=j/120,w=Math.exp(-.5*(offset/sigma)**2);
        const v=sample.evaluate(((time+offset)%duration+duration)%duration);
        const sign=rotation&&q.fromArray(v).dot(base)<0?-1:1;
        for(let k=0;k<size;k++)sum[k]+=v[k]*w*sign;
        total+=w;
      }
      for(let k=0;k<size;k++)values[i*size+k]=sum[k]/total;
      if(rotation)q.fromArray(values,i*size).normalize().toArray(values,i*size);
    }
    values.set(values.subarray(0,size),steps*size);
    if(!rotation)for(let axis=0;axis<3;axis++){
      let oldMean=0,newMean=0;
      for(let i=axis;i<track.values.length;i+=3)oldMean+=track.values[i]/(track.values.length/3);
      for(let i=axis;i<values.length;i+=3)newMean+=values[i]/(values.length/3);
      for(let i=axis;i<values.length;i+=3)values[i]+=oldMean-newMean;
    }
    return rotation?new T.QuaternionKeyframeTrack(track.name,times,values):new T.VectorKeyframeTrack(track.name,times,values);
  });
  return new T.AnimationClip(clip.name,duration,tracks,clip.blendMode);
}
